import {
  IConnectionRequest,
  IConnectionRequestBasicInformation,
  IConnectionRequestFilters,
  IConnectionRequestUpdateDto,
  IConnectionRequestWithHostCreateDto,
  IConnectionRequestWithRelations,
  IProposedConnectionDateReserved,
} from '@connection-request/interfaces';
import {PrismaFilter} from '@shared/services/base/interfaces/prisma-filter.interface';
import {Action, Selector, State, StateContext} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {environment} from '@env/environment';
import {BaseService} from '@shared/services';
import {ErrorHandlerService} from '@shared/services/error-handler.service';
import {DataBaseServiceResponse} from '@shared/services/base/interfaces/data-base-service-response.interface';
import {firstValueFrom} from 'rxjs';
import {IProposedConnectionGuest} from '@connection-request/interfaces/proposed-connection-guest.interface';
import {UtilityClass} from '@shared/utils/utility.class';
import {IProposedConnectionDate} from '@connection-request/interfaces/proposed-connection-date.interface';
import {BadRequestException} from '@shared/class/errors/bad-request.exception';
import {PLATFORM_MESSAGES} from '@shared/config';
import {ConnectionProposedByEnum, ConnectionRequestStatusEnum} from '@connection-request/enums';
import {cloneDeep} from 'lodash';

export interface IConnectionRequestState {
  list: IConnectionRequestWithRelations[];
  acceptedDateList: IConnectionRequestWithRelations[];
  selectedId: string;
  count: number;
  proposedDateAvailabilityRequest: boolean;
  reservedHourByHost: IProposedConnectionDateReserved[];
  reservedHourByInvited: IProposedConnectionDateReserved[];
  connectionRequestFeedback: IConnectionRequestWithRelations | null;
  connectionsRequestBasicInformation: IConnectionRequestBasicInformation[];

  connectionRequestToCopy: IConnectionRequestWithRelations | null;
}

const _DEFAULT_DATA: IConnectionRequestState = {
  list: [],
  acceptedDateList: [],
  selectedId: '',
  count: 0,
  proposedDateAvailabilityRequest: false,
  reservedHourByHost: [],
  reservedHourByInvited: [],
  connectionRequestFeedback: null,
  connectionsRequestBasicInformation: [],

  connectionRequestToCopy: null
}

export namespace ConnectionRequestActions {

  export class SetToCopy {
    static readonly type: string = '[ConnectionRequest] Set To Copy';
    constructor(public connectionRequest: Partial<IConnectionRequestWithRelations> | null) {}
  }

  export class GetList {
    static readonly type: string = '[ConnectionRequest] Get Connection Request List';
    constructor(public filter?: PrismaFilter<IConnectionRequestWithRelations>) {}
  }

  export class GetCount {
    static readonly type: string = '[ConnectionRequest] Get Connection Request Count';
    constructor(public filter?: PrismaFilter<IConnectionRequestWithRelations>) {}
  }

  export class GetSortedList {
    static readonly type: string = '[ConnectionRequest] Get Connection Request Sorted List';
    constructor(public filter?: IConnectionRequestFilters) {}
  }

  export class GetSortedCount {
    static readonly type: string = '[ConnectionRequest] Get Connection Request Sorted Count';
    constructor(public filter?: IConnectionRequestFilters) {}
  }

  export class GetListAcceptedDate {
    static readonly type: string = '[ConnectionRequest] Get Connection Request Accepted Date List';
    constructor(public filter?: IConnectionRequestFilters) {}
  }

  export class GetListBasicInformation {
    static readonly type: string = '[ConnectionRequest] Get Connection Request List Basic Information';
    constructor(public filter?: PrismaFilter<IConnectionRequest>) {}
  }

  export class GetById {
    static readonly type: string = '[ConnectionRequest] Get Connection Request By Id';
    constructor(public connectionRequestId: string, public filter?: PrismaFilter<IConnectionRequest>) {}
  }

  export class GetFeedbackById {
    static readonly type: string = '[ConnectionRequest] Get Feedback Connection Request By Id';
    constructor(public connectionRequestId: string, public filter?: PrismaFilter<IConnectionRequest>) {}
  }

  export class Post {
    static readonly type: string = '[ConnectionRequest] Post Connection Request';
    constructor(public connectionRequest: IConnectionRequestWithHostCreateDto) {}
  }

  // CONNECTION REQUEST V2
  export class Create {
    static readonly type: string = '[ConnectionRequest] Create Connection Request';
    constructor(public connectionRequest: IConnectionRequestWithHostCreateDto) {}
  }

  export class Patch {
    static readonly type: string = '[ConnectionRequest] Patch Connection Request';
    constructor(public id: string, public connectionRequest: IConnectionRequestUpdateDto) {}
  }

  // CONNECTION REQUEST V2
  export class Update {
    static readonly type: string = '[ConnectionRequest] Update Connection Request';
    constructor(public id: string, public connectionRequest: IConnectionRequestUpdateDto) {}
  }

  export class AddGuest {
    static readonly type: string = '[ConnectionRequest] Add Guest Connection Request';
    constructor(public connectionId: string, public data: any) {}
  }

  export class DeleteGuest {
    static readonly type: string = '[ConnectionRequest] Delete Guest Connection Request';
    constructor(public connectionId: string, public guestId: string) {}
  }

  export class Cancel {
    static readonly type: string = '[ConnectionRequest] Connection Request Cancel';
    constructor(public connectionRequestId: string) {}
  }

  export class Finish {
    static readonly type: string = '[ConnectionRequest] Connection Request Finish';
    constructor(public connectionRequestId: string) {}
  }

  export class GetReservedHours {
    static readonly type: string = '[ConnectionRequest] Get Reserved Hours';
    constructor(public year: number, public month: number, public day: number, public hostOrganizationId: string, public invitedOrganizationId: string, public excludeConnectionRequestId?: string) {}
  }

  export class AcceptProposedDate {
    static readonly type: string = '[ConnectionRequest] Accept Proposed Date Connection Request';
    constructor(public connectionId: string, public dateId: string, public startDate: string, public endDate: string) {}
  }

  export class DeleteProposedDate {
    static readonly type: string = '[ConnectionRequest] Delete Proposed Date Connection Request';
    constructor(public connectionId: string, public dateId: string) {}
  }

  export class DeleteAllProposedDates {
    static readonly type: string = '[ConnectionRequest] Delete All Proposed Date Connection Request';
    constructor(public connectionId: string) {}
  }

  export class CheckAvailabilityProposedDate {
    static readonly type: string = '[ConnectionRequest] Check Availability Proposed Date Connection Request';
    constructor(public organizationId: string, public connectionId: string, public dates: { startDate: string, endDate: string }) {}
  }

  export class SetSelectedId {
    static readonly type: string = '[ConnectionRequest] Set Selected Id Connection Request';
    constructor(public connectionRequestId: string) {}
  }

  export class Reset {
    static readonly type: string = '[ConnectionRequest] Reset';
    constructor() {}
  }
}

@State<IConnectionRequestState>({
  name: 'ConnectionRequestState',
  defaults: _DEFAULT_DATA
})
@Injectable()
export class ConnectionRequestState {
  private readonly SERVER: string = environment.SERVER;

  constructor(
    private baseService: BaseService,
    private errorHandlerService: ErrorHandlerService,
  ) {}

  @Selector()
  static getConnectionRequestList(state: IConnectionRequestState): IConnectionRequestWithRelations[] {
    return state.list;
  }

  @Selector()
  static getConnectionRequestListAcceptedDate(state: IConnectionRequestState): IConnectionRequestWithRelations[] {
    return state.acceptedDateList;
  }

  @Selector()
  static getConnectionRequestListBasicInformation(state: IConnectionRequestState): IConnectionRequestBasicInformation[] {
    return state.connectionsRequestBasicInformation;
  }

  @Selector()
  static getConnectionRequestSelected({list, selectedId}: IConnectionRequestState): IConnectionRequestWithRelations | undefined {
    return list.find((connectionRequest: IConnectionRequest): boolean => connectionRequest.id === selectedId);
  }

  @Selector()
  static getCount({count}: IConnectionRequestState): number {
    return count;
  }

  @Selector()
  static getProposedDateAvailability({proposedDateAvailabilityRequest}: IConnectionRequestState): boolean {
    return proposedDateAvailabilityRequest;
  }

  @Selector()
  static getReservedHoursByHost({reservedHourByHost}: IConnectionRequestState): IProposedConnectionDateReserved[] {
    return reservedHourByHost;
  }

  @Selector()
  static getReservedHoursByInvited({reservedHourByInvited}: IConnectionRequestState): IProposedConnectionDateReserved[] {
    return reservedHourByInvited;
  }

  @Selector()
  static getConnectionRequestFeedback({connectionRequestFeedback}: IConnectionRequestState): IConnectionRequestWithRelations | null{
    return connectionRequestFeedback;
  }

  @Selector()
  static getConnectionRequestToCopy({connectionRequestToCopy}: IConnectionRequestState): IConnectionRequestWithRelations | null{
    return connectionRequestToCopy;
  }


  @Action(ConnectionRequestActions.GetListAcceptedDate)
  async getListAcceptedDate(
    {patchState}: StateContext<IConnectionRequestState>,
    {filter}: ConnectionRequestActions.GetListAcceptedDate
  ): Promise<void> {
    const response: DataBaseServiceResponse<IConnectionRequestWithRelations[]> = await firstValueFrom(this.baseService.get<IConnectionRequestWithRelations[]>(`${this.SERVER}/connection-requests/accepted-date`, filter));
    if (response.error) throw this.errorHandlerService.createRequestException(response.serverResponse);
    patchState({
      acceptedDateList: response.entity ?? [],
    });
  }


  @Action(ConnectionRequestActions.GetListBasicInformation)
  async getListBasicInformation(
    {patchState}: StateContext<IConnectionRequestState>,
    {filter}: ConnectionRequestActions.GetListBasicInformation
  ): Promise<void> {
    const response: DataBaseServiceResponse<IConnectionRequestBasicInformation[]> = await firstValueFrom(this.baseService.get<IConnectionRequestBasicInformation[]>(`${this.SERVER}/connection-requests/basic-information`, filter));
    if (response.error) throw this.errorHandlerService.createRequestException(response.serverResponse);
    patchState({
      connectionsRequestBasicInformation: response.entity ?? []
    })
  }

  @Action(ConnectionRequestActions.GetById)
  async getById(
    {patchState}: StateContext<IConnectionRequestState>,
    {connectionRequestId, filter}: ConnectionRequestActions.GetById
  ): Promise<void> {
    const response: DataBaseServiceResponse<IConnectionRequestWithRelations> = await firstValueFrom(this.baseService.get<IConnectionRequestWithRelations>(`${this.SERVER}/connection-requests/${connectionRequestId}`, filter));
    if (response.error) throw this.errorHandlerService.createRequestException(response.serverResponse);
    patchState({
      list: [response.entity!],
      selectedId: response.entity?.id,
    })
  }

  @Action(ConnectionRequestActions.GetFeedbackById)
  async GetFeedbackById(
    {patchState, getState}: StateContext<IConnectionRequestState>,
    {connectionRequestId, filter}: ConnectionRequestActions.GetFeedbackById
  ): Promise<void> {
    const connection: IConnectionRequestWithRelations | null = getState().connectionRequestFeedback;
    if (connection && connection.id === connectionRequestId) return;
    const response: DataBaseServiceResponse<IConnectionRequestWithRelations> = await firstValueFrom(this.baseService.get<IConnectionRequestWithRelations>(`${this.SERVER}/connection-requests/${connectionRequestId}`, filter));
    if (response.error) throw this.errorHandlerService.createRequestException(response.serverResponse);
    patchState({
      connectionRequestFeedback: response.entity!,
    });
  }

  @Action(ConnectionRequestActions.GetList)
  async getList(
    {patchState}: StateContext<IConnectionRequestState>,
    {filter}: ConnectionRequestActions.GetList
  ): Promise<void> {
    const response: DataBaseServiceResponse<IConnectionRequestWithRelations[]> = await firstValueFrom(this.baseService.get<IConnectionRequestWithRelations[]>(`${this.SERVER}/connection-requests`, filter));
    if (response.error) throw this.errorHandlerService.createRequestException(response.serverResponse);

    patchState({
      list: response.entity ?? []
    })
  }

  @Action(ConnectionRequestActions.GetCount)
  async getCount(
    {patchState}: StateContext<IConnectionRequestState>,
    {filter}: ConnectionRequestActions.GetCount
  ): Promise<void> {
    const response: DataBaseServiceResponse<number> = await firstValueFrom(this.baseService.count<IConnectionRequest>(`${this.SERVER}/connection-requests/count`, filter));
    if (response.error) throw this.errorHandlerService.createRequestException(response.serverResponse);
    patchState({
      count: response.entity ?? 0
    })
  }

  @Action(ConnectionRequestActions.GetSortedList)
  async getSortedList(
    {patchState}: StateContext<IConnectionRequestState>,
    {filter}: ConnectionRequestActions.GetSortedList
  ): Promise<void> {
    const response: DataBaseServiceResponse<IConnectionRequestWithRelations[]> = await firstValueFrom(this.baseService.get<IConnectionRequestWithRelations[]>(`${this.SERVER}/connection-requests/sorted`, filter));
    if (response.error) throw this.errorHandlerService.createRequestException(response.serverResponse);

    patchState({
      list: response.entity ?? []
    })
  }

  @Action(ConnectionRequestActions.GetSortedCount)
  async getSortedCount(
    {patchState}: StateContext<IConnectionRequestState>,
    {filter}: ConnectionRequestActions.GetSortedCount
  ): Promise<void> {
    const response: DataBaseServiceResponse<number> = await firstValueFrom(this.baseService.count<IConnectionRequest>(`${this.SERVER}/connection-requests/sorted/count`, filter));
    if (response.error) throw this.errorHandlerService.createRequestException(response.serverResponse);
    patchState({
      count: response.entity ?? 0
    })
  }

  @Action(ConnectionRequestActions.Post)
  async post(
    {patchState, getState}: StateContext<IConnectionRequestState>,
    {connectionRequest}: ConnectionRequestActions.Post
  ): Promise<void> {
    const response: DataBaseServiceResponse<IConnectionRequestWithRelations> = await firstValueFrom(this.baseService.post<IConnectionRequestWithRelations>(`${this.SERVER}/connection-requests`, connectionRequest));
    if (response.error) {
      if (response.message?.includes('(CR008) ConnectionRequest already have an active')) {
        throw new BadRequestException(PLATFORM_MESSAGES.CONNECTION_REQUEST.ALREADY_HAVE_ACTIVE_CONNECTION);
      }
      throw this.errorHandlerService.createRequestException(response.serverResponse);
    }
    patchState({
      list: UtilityClass.updateOrPushItems<IConnectionRequestWithRelations>(getState().list, response.entity!, 'id'),
      selectedId: response.entity?.id
    })
  }

  @Action(ConnectionRequestActions.Create)
  async create(
    {patchState, getState}: StateContext<IConnectionRequestState>,
    {connectionRequest}: ConnectionRequestActions.Create
  ): Promise<void> {
    const response: DataBaseServiceResponse<IConnectionRequestWithRelations> = await firstValueFrom(this.baseService.post<IConnectionRequestWithRelations>(`${this.SERVER}/connection-requests/v2`, connectionRequest));
    if (response.error) {
      if (response.message?.includes('(CR008) ConnectionRequest already have an active')) {
        throw new BadRequestException(PLATFORM_MESSAGES.CONNECTION_REQUEST.ALREADY_HAVE_ACTIVE_CONNECTION);
      }
      throw this.errorHandlerService.createRequestException(response.serverResponse);
    }
    patchState({
      list: UtilityClass.updateOrPushItems<IConnectionRequestWithRelations>(getState().list, response.entity!, 'id'),
      selectedId: response.entity?.id
    });
  }

  @Action(ConnectionRequestActions.Patch)
  async patch(
    {patchState, getState}: StateContext<IConnectionRequestState>,
    {id, connectionRequest}: ConnectionRequestActions.Patch
  ): Promise<void> {
    const response: DataBaseServiceResponse<IConnectionRequestWithRelations> = await firstValueFrom(this.baseService.patch<IConnectionRequestWithRelations>(`${this.SERVER}/connection-requests/${id}`, connectionRequest));
    if (response.error) throw this.errorHandlerService.createRequestException(response.serverResponse);
    patchState({
      list: UtilityClass.updateOrPushItems<IConnectionRequestWithRelations>(getState().list, response.entity!, 'id'),
      selectedId: response.entity?.id
    })
  }

  @Action(ConnectionRequestActions.Update)
  async update(
    {patchState, getState}: StateContext<IConnectionRequestState>,
    {id, connectionRequest}: ConnectionRequestActions.Update
  ): Promise<void> {
    const response: DataBaseServiceResponse<IConnectionRequestWithRelations> = await firstValueFrom(this.baseService.patch<IConnectionRequestWithRelations>(`${this.SERVER}/connection-requests/v2/${id}`, connectionRequest));
    if (response.error) throw this.errorHandlerService.createRequestException(response.serverResponse);
    patchState({
      list: UtilityClass.updateOrPushItems<IConnectionRequestWithRelations>(getState().list, response.entity!, 'id'),
      selectedId: response.entity?.id
    })
  }

  @Action(ConnectionRequestActions.AddGuest)
  async addGuest(
    {patchState, getState}: StateContext<IConnectionRequestState>,
    {connectionId, data}: ConnectionRequestActions.AddGuest
  ): Promise<void> {
    const response: DataBaseServiceResponse<IProposedConnectionGuest[]> = await firstValueFrom(this.baseService.put<IProposedConnectionGuest[]>(`${this.SERVER}/connection-requests/${connectionId}/guests`, data));
    if (response.error) throw this.errorHandlerService.createRequestException(response.serverResponse);
    const list: IConnectionRequestWithRelations[] = [...getState().list];
    const connectionIndex: number = list.findIndex(({id}): boolean => id === getState().selectedId)
    if (connectionIndex === -1) return;
    list[connectionIndex].ProposedConnectionGuest = response.entity!;
    patchState({
      list
    })
  }

  @Action(ConnectionRequestActions.DeleteGuest)
  async deleteGuest(
    {patchState, getState}: StateContext<IConnectionRequestState>,
    {connectionId, guestId}: ConnectionRequestActions.DeleteGuest
  ): Promise<void> {
    const response: DataBaseServiceResponse<IProposedConnectionGuest[]> = await firstValueFrom(this.baseService.delete<IProposedConnectionGuest[]>(`${this.SERVER}/connection-requests/${connectionId}/guests/${guestId}`));
    if (response.error) throw this.errorHandlerService.createRequestException(response.serverResponse);
    const list: IConnectionRequestWithRelations[] = [...getState().list];
    const connectionIndex: number = list.findIndex(({id}): boolean => id === getState().selectedId)
    if (connectionIndex === -1) return;
    list[connectionIndex].ProposedConnectionGuest = UtilityClass.deleteItemByProp(list[connectionIndex].ProposedConnectionGuest!, 'id', guestId)
    patchState({
      list
    });
  }

  @Action(ConnectionRequestActions.Cancel)
  async cancel(
    {patchState, getState}: StateContext<IConnectionRequestState>,
    {connectionRequestId}: ConnectionRequestActions.Cancel
  ): Promise<void> {
    const response: DataBaseServiceResponse<IConnectionRequestWithRelations> = await firstValueFrom(this.baseService.patch<IConnectionRequestWithRelations>(`${this.SERVER}/connection-requests/${connectionRequestId}/cancel`, void 0));
    if (response.error) throw this.errorHandlerService.createRequestException(response.serverResponse);
    patchState({
      list: UtilityClass.updateOrPushItems<IConnectionRequestWithRelations>(getState().list, response.entity!, 'id'),
    })
  }

  @Action(ConnectionRequestActions.Finish)
  async finish(
    {patchState, getState}: StateContext<IConnectionRequestState>,
    {connectionRequestId}: ConnectionRequestActions.Finish
  ): Promise<void> {
    const response: DataBaseServiceResponse<IConnectionRequest> = await firstValueFrom(this.baseService.patch<IConnectionRequest>(`${this.SERVER}/connection-requests/${connectionRequestId}/finish`, void 0));
    if (response.error) throw this.errorHandlerService.createRequestException(response.serverResponse);
    const list: IConnectionRequestWithRelations[] = [...getState().list];
    const connectionIndex: number = list.findIndex(({id}): boolean => id === getState().selectedId)
    if (connectionIndex === -1) return;
    list[connectionIndex].status = ConnectionRequestStatusEnum.FINISHED;
    patchState({
      list
    });
  }

  @Action(ConnectionRequestActions.AcceptProposedDate)
  async acceptProposedDate(
    {patchState, getState}: StateContext<IConnectionRequestState>,
    {connectionId, dateId, startDate, endDate}: ConnectionRequestActions.AcceptProposedDate
  ): Promise<void> {
    const response: DataBaseServiceResponse<IProposedConnectionDate> = await firstValueFrom(this.baseService.patch<IProposedConnectionDate>(`${this.SERVER}/connection-requests/${connectionId}/dates/${dateId}/accept`, {startDate, endDate}));
    if (response.error) throw this.errorHandlerService.createRequestException(response.serverResponse);
    const list: IConnectionRequestWithRelations[] = [...getState().list];
    const connectionIndex: number = list.findIndex(({id}): boolean => id === getState().selectedId)
    if (connectionIndex === -1) return;
    const proposedConnectionDateIndex: number = list[connectionIndex].ProposedConnectionDate!.findIndex(({id}) => id === dateId);
    if (proposedConnectionDateIndex === -1) return;

    const dates: IProposedConnectionDate[] = list[connectionIndex].ProposedConnectionDate ?? [];
    dates[proposedConnectionDateIndex] = response.entity!;

    list[connectionIndex] = {
      ...list[connectionIndex],
      status: ConnectionRequestStatusEnum.ACCEPTED,
      ProposedConnectionDate: [...dates],
    };
    patchState({
      list
    });
  }

  @Action(ConnectionRequestActions.DeleteProposedDate)
  async deleteProposedDate(
    {patchState, getState}: StateContext<IConnectionRequestState>,
    {connectionId, dateId}: ConnectionRequestActions.DeleteProposedDate
  ): Promise<void> {
    const response: DataBaseServiceResponse<IProposedConnectionDate> = await firstValueFrom(this.baseService.delete<IProposedConnectionDate>(`${this.SERVER}/connection-requests/${connectionId}/dates/${dateId}`));
    if (response.error) throw this.errorHandlerService.createRequestException(response.serverResponse);
    // const list: IConnectionRequestWithRelations[] = [...getState().list];
    // const connectionIndex: number = list.findIndex(({id}): boolean => id === getState().selectedId)
    // if (connectionIndex === -1) return;
    // list[connectionIndex].ProposedConnectionDate = UtilityClass.deleteItemByProp(list[connectionIndex].ProposedConnectionDate!, 'id', dateId)
    // patchState({
    //   list
    // })
  }

  @Action(ConnectionRequestActions.DeleteAllProposedDates)
  async deleteAllProposedDates(
    {patchState, getState}: StateContext<IConnectionRequestState>,
    {connectionId}: ConnectionRequestActions.DeleteAllProposedDates
  ): Promise<void> {
    const response: DataBaseServiceResponse<IProposedConnectionDate> = await firstValueFrom(this.baseService.delete<IProposedConnectionDate>(`${this.SERVER}/connection-requests/${connectionId}/dates`));
    if (response.error) throw this.errorHandlerService.createRequestException(response.serverResponse);
    const list: IConnectionRequestWithRelations[] = [...getState().list];
    const connectionIndex: number = list.findIndex(({id}): boolean => id === getState().selectedId)
    if (connectionIndex === -1) return;
    list[connectionIndex].ProposedConnectionDate = []

    patchState({list});
  }

  @Action(ConnectionRequestActions.CheckAvailabilityProposedDate)
  async checkAvailabilityProposedDate(
    {patchState, getState}: StateContext<IConnectionRequestState>,
    {organizationId, connectionId, dates}: ConnectionRequestActions.CheckAvailabilityProposedDate
  ): Promise<void> {
    const response: DataBaseServiceResponse<any> = await firstValueFrom(this.baseService.patch<any>(`${this.SERVER}/organizations/${organizationId}/connection-requests/${connectionId}/dates/check-availability`, dates));
    if (response.error) throw this.errorHandlerService.createRequestException(response.serverResponse);
    patchState({
      proposedDateAvailabilityRequest: response.entity.available
    })
  }

  @Action(ConnectionRequestActions.GetReservedHours)
  async getReservedHours(
    {patchState}: StateContext<IConnectionRequestState>,
    {year, month, day, hostOrganizationId, invitedOrganizationId, excludeConnectionRequestId}: ConnectionRequestActions.GetReservedHours
  ): Promise<void> {
    const response: DataBaseServiceResponse<any> = await firstValueFrom(this.baseService.get<any>(`${this.SERVER}/connection-requests/dates/busy?year=${year}&month=${month}&day=${day}&hostOrganizationId=${hostOrganizationId}&invitedOrganizationId=${invitedOrganizationId}&excludeConnectionRequestId=${excludeConnectionRequestId ?? void 0}`));
    if (response.error) throw this.errorHandlerService.createRequestException(response.serverResponse);
    const hours: IProposedConnectionDateReserved[] = response.entity.busyDates || [];
    patchState({
      reservedHourByHost: hours.filter((hour: IProposedConnectionDateReserved): boolean => hour.proposedBy === ConnectionProposedByEnum.HOST_ORGANIZATION),
      reservedHourByInvited: hours.filter((hour: IProposedConnectionDateReserved): boolean => hour.proposedBy === ConnectionProposedByEnum.INVITED_ORGANIZATION),
    })
  }

  @Action(ConnectionRequestActions.SetSelectedId)
  async setSelectedId(
    {patchState}: StateContext<IConnectionRequestState>,
    {connectionRequestId}: ConnectionRequestActions.SetSelectedId
  ): Promise<void> {
    patchState({
      selectedId: connectionRequestId
    })
  }

  @Action(ConnectionRequestActions.SetToCopy)
  async setToCopy(
    {patchState}: StateContext<IConnectionRequestState>,
    {connectionRequest}: ConnectionRequestActions.SetToCopy
  ): Promise<void> {
    patchState({
      connectionRequestToCopy: cloneDeep(connectionRequest as IConnectionRequestWithRelations)
    });
  }

  @Action(ConnectionRequestActions.Reset)
  async reset(
    {patchState}: StateContext<IConnectionRequestState>,
    _: ConnectionRequestActions.Reset
  ): Promise<void> {
    patchState({
      list: _DEFAULT_DATA.list,
      acceptedDateList: _DEFAULT_DATA.acceptedDateList,
      selectedId: _DEFAULT_DATA.selectedId,
      count: _DEFAULT_DATA.count,
      proposedDateAvailabilityRequest: _DEFAULT_DATA.proposedDateAvailabilityRequest,
      reservedHourByHost: _DEFAULT_DATA.reservedHourByHost,
      reservedHourByInvited: _DEFAULT_DATA.reservedHourByInvited,
      connectionRequestFeedback: _DEFAULT_DATA.connectionRequestFeedback,
      connectionsRequestBasicInformation: _DEFAULT_DATA.connectionsRequestBasicInformation,
    });
  }
}
